import { AccountInfo } from '@azure/msal-browser'
import { Agent } from '@typings/dify'
import { AccentColor, Theme } from '@typings/index'
import { SlidersHorizontal , LayoutDashboard, Settings, User, LogOut } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ColorPicker from './ColorPicker'
import LanguageToogle from './LanguageToogle'
import ThemeToggle from './ThemeToggle'
import UserBadge from './UserBadge'

import { logout } from '../config/auth.config'

interface UserMenuProps {
  theme: Theme
  setTheme: (theme: Theme) => void
  accentColor: AccentColor
  setAccentColor: (color: AccentColor) => void
  onShowDashboard: () => void
  account: AccountInfo | null
  agents: Agent[],
  showAgentsManagement?: boolean
}

const UserMenu = ({
  theme,
  setTheme,
  accentColor,
  setAccentColor,
  onShowDashboard,
  account,
  agents,
  showAgentsManagement = true,
}: UserMenuProps) => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)

  const totalLikes = agents.reduce((sum, agent) => sum + (agent.stats?.likeCount || 0), 0)

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={menuRef}>
      <div className="flex items-center">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-full hover:bg-gray-100 dark:text-white dark:hover:bg-gray-800 flex items-center space-x-2"
        >
          <User size={20} />
          <span className="text-sm font-medium">{account?.name ? account?.name : account?.username}</span>

          {totalLikes >= 10 && <UserBadge totalLikes={totalLikes} showTooltip={false} />}
        </button>
      </div>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:text-white dark:bg-gray-800 rounded-lg shadow-lg p-4 z-50">
          <div className="space-y-4">
            <button
              onClick={() => {
                onShowDashboard()
                setIsOpen(false)
              }}
              className="flex items-center space-x-2 w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <LayoutDashboard size={20} />
              <span>{t('user_menu.dashboard')}</span>
            </button>

            {showAgentsManagement && (
              <button
                onClick={() => {
                  navigate("/agents-management")
                }}
                className="flex items-center space-x-2 w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                <SlidersHorizontal  size={20} />
                <span>{t('user_menu.agents_management')}</span>
              </button>
            )}

            <button
                onClick={() => {
                  logout()
                }}
                className="flex items-center space-x-2 w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                <LogOut size={20} />
                <span>{t('user_menu.logout')}</span>
              </button>

            <div className="border-t dark:border-gray-700 pt-4 ">
              <div className="flex items-center space-x-2 mb-4">
                <Settings size={16} />
                <span className="font-medium">{t('user_menu.settings')}</span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('user_menu.theme')}</label>
                  <ThemeToggle theme={theme} setTheme={setTheme} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('user_menu.language')}</label>
                  <LanguageToogle />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-4">{t('user_menu.accent_color')}</label>
                  <ColorPicker value={accentColor} onChange={setAccentColor} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserMenu
